// frontend/src/components/community/EditPostModal.js
import React, { useState, useEffect } from 'react';
import { X, Image, AlertCircle, FileText, Loader } from 'lucide-react';
import useCommunityStore from '../../store/communityStore';
import { toast } from 'react-hot-toast';

const EditPostModal = ({ post, onClose, onPostUpdated }) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updatePost } = useCommunityStore();

  useEffect(() => {
    if (post) {
      setContent(post.content || '');
    }
  }, [post]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (content.trim().length < 1) {
      toast.error('Post content cannot be empty');
      return;
    }

    setIsSubmitting(true);
    try {
      await updatePost(post.id, content, post.mediaUrl);
      
      toast.success('Post updated successfully!');
      
      if (onPostUpdated) {
        onPostUpdated();
      }
      
      onClose();
    } catch (error) {
      console.error('Update post error:', error);
      toast.error(error.message || 'Failed to update post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = content.length;
  const maxCharacters = 1000;
  const percentage = (characterCount / maxCharacters) * 100;

  if (!post) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 border border-primary-100/30">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <div className="flex items-center mb-2">
                <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Edit Post
                </h3>
              </div>
              
              <form onSubmit={handleSubmit} className="mt-4 space-y-5">
                {/* Content */}
                <div className="relative">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="What's on your mind? Share your business insights, tips, or questions..."
                    className="block w-full px-4 py-3 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 min-h-[150px] resize-none"
                    maxLength={maxCharacters}
                    disabled={isSubmitting}
                  />
                  
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-[#E8ECF5] px-3 py-1.5 rounded-lg shadow-neomorph-inset">
                        <span className={`text-xs font-medium ${
                          characterCount > maxCharacters * 0.9 ? 'text-orange-600' : 'text-gray-600'
                        }`}>
                          {characterCount}/{maxCharacters}
                        </span>
                      </div>
                    </div>

                    {characterCount > maxCharacters * 0.8 && (
                      <div className={`flex items-center px-3 py-1.5 rounded-lg shadow-neomorph-inset ${
                        characterCount > maxCharacters * 0.9 ? 'bg-orange-50' : 'bg-yellow-50'
                      }`}>
                        <AlertCircle className={`h-3.5 w-3.5 mr-1.5 ${
                          characterCount > maxCharacters * 0.9 ? 'text-orange-600' : 'text-yellow-600'
                        }`} />
                        <span className={`text-xs font-medium ${
                          characterCount > maxCharacters * 0.9 ? 'text-orange-700' : 'text-yellow-700'
                        }`}>
                          {characterCount > maxCharacters * 0.9 ? 'Almost full!' : 'Good length'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="mt-2 h-1 bg-[#E8ECF5] rounded-full overflow-hidden shadow-neomorph-inset">
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${
                        percentage > 90 ? 'bg-orange-500' : 
                        percentage > 70 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>

                {/* Current Media Preview */}
                {post.mediaUrl && (
                  <div className="bg-[#E8ECF5] rounded-xl p-4 shadow-neomorph-inset border border-gray-200">
                    <h4 className="text-xs font-medium text-gray-600 mb-3">Current Media</h4>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-white rounded-lg shadow-neomorph-inset flex items-center justify-center">
                          {post.mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <Image className="h-5 w-5 text-primary-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate max-w-[200px]">
                            {post.mediaUrl.split('/').pop()}
                          </p>
                          <p className="text-xs text-gray-500">
                            Media attached to post
                          </p>
                        </div>
                      </div>
                      <a
                        href={post.mediaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-primary-600 border border-gray-100"
                      >
                        View
                      </a>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="px-4 py-2.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || content.trim().length < 1}
                    className="px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Post'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default EditPostModal;